import { Button } from '@bennows/boows-react';
import { Zap } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShellBilling } from './useShellBilling';

/** Loaded on the first tap: the sheet's tables stay out of the shell bundle. */
const CreditSheet = lazy(() => import('./CreditSheet').then((module) => ({ default: module.CreditSheet })));

const TONE = { low: 'ui-credit-pill ui-credit-pill--low', exhausted: 'ui-credit-pill ui-credit-pill--exhausted' } as const;

/**
 * The credit balance in the frame (style guide 2.10, S150-1, S170-3): shown
 * only while a metered account runs low or has nothing left, tinted amber or
 * red; a tap opens the credit sheet. Nothing otherwise - at a normal balance,
 * for exempt accounts, members without `ai:run`, an unlinked tenant, while
 * loading or on a failed request: the balance stays on "Tarif & Verbrauch"
 * (`/settings/billing`), which also has those states.
 */
export function CreditPill() {
  const { t } = useTranslation();
  const { canUseAi, billing } = useShellBilling();
  const [open, setOpen] = useState(false);
  const data = billing.data;
  const level = data?.level;
  if (!canUseAi || !data?.linked || data.mode === 'exempt' || (level !== 'low' && level !== 'exhausted')) return null;
  const text = t('billing.pill.metered', { count: data.available ?? 0 });
  return (
    <>
      <Button
        variant="secondary"
        className={TONE[level]}
        iconPrefix={<Zap aria-hidden="true" size={16} />}
        aria-label={t('billing.pill.label', { credits: text })}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {text}
      </Button>
      {open ? (
        <Suspense fallback={null}>
          <CreditSheet billing={data} onClose={() => setOpen(false)} />
        </Suspense>
      ) : null}
    </>
  );
}
